import { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { getBoardSummaryApi } from '../../../services/api';
import { KANBAN_STAGES } from '../../../constants/constants';
import { useDispatch } from 'react-redux';
import { updateState } from '../../../redux/commonSlice';

/**
 * Fetches the board_summary endpoint which returns total counts + up to 3
 * preview events per column.  Fast — designed to render the board instantly.
 *
 * Returns:
 *   summaryByKey  — { production: { total, events[] }, editing: ..., ... }
 *   isLoading, isError, refetch, isFetching
 */
export const useKanbanBoardSummary = ({ orgId, name, editorId, eventTypeId, dueWithinHours, quickTurn } = {}) => {

  const dispatch = useDispatch();
  const params = {
    ...(orgId ? { organisation_id: orgId } : {}),
    ...(name ? { name } : {}),
    ...(editorId && editorId !== 'unassigned' ? { internal_user_id: editorId } : {}),
    ...(editorId === 'unassigned' ? { unassigned: true } : {}),
    ...(eventTypeId ? { event_type_id: eventTypeId } : {}),
    ...(dueWithinHours && dueWithinHours !== 'overdue' ? { due_within_hours: dueWithinHours } : {}),
    ...(dueWithinHours === 'overdue' ? { overdue: true } : {}),
    ...(quickTurn ? { quick_turn: true } : {}),
  };

  const query = useQuery(
    ['kanban-board-summary', orgId, name, editorId, eventTypeId, dueWithinHours, quickTurn],
    () => getBoardSummaryApi(params),
    {
      keepPreviousData: true,
      staleTime: 0,
    }
  );

  const summaryByKey = useMemo(() => {
    // Initialise all stages with empty defaults.
    const defaults = {};
    KANBAN_STAGES.forEach((s) => {
      defaults[s.key] = { total: 0, events: [] };
    });

    const raw = query.data?.data?.data?.summary;
    if (!raw) return defaults;

    const result = { ...defaults };
    KANBAN_STAGES.forEach((s) => {
      if (raw[s.key]) {
        result[s.key] = {
          total: raw[s.key].total ?? 0,
          events: raw[s.key].events ?? [],
        };
      }
    });

    return result;
  }, [query.data]);

  // When the board summary API includes pending_quick_turn_count, sync it to
  // redux so the badge in CustomUserCard stays up-to-date without a separate
  // fetch. The list API in usePendingQuickTurnActions does the same on the
  // quick-turn page, so whichever resolves last wins.
  useEffect(() => {
    const raw = query.data?.data?.data;
    if (raw?.pending_quick_turn_count != null) {
      dispatch(updateState({ pending_quick_turn_count: raw.pending_quick_turn_count }));
    }
    if (raw?.quick_turn_quota != null) {
      dispatch(updateState({ quick_turn_quota: raw.quick_turn_quota }));
    }
  }, [query.data]);

  return { ...query, summaryByKey };
};
